/**
 * Pure helpers behind the "Held-out tests" dialog (TRA-1434), kept out of
 * the components so they are testable without a DOM.
 *
 * Held-out tests are the benchmark tests a team's training dataset
 * validates on and never trains on. Staff pick them from the tests the
 * latest scoring night scored; the api saves the whole list, by each
 * test's path in the repository.
 */

import type { HeldOutTestsResponse } from '@/api/generated/model'

/**
 * What is ticked when the dialog opens: the saved tests. That includes
 * saved tests the latest night did not score, which stay saved until
 * someone unticks them.
 */
export function savedSelection(data: HeldOutTestsResponse): string[] {
  const scored = data.tests.filter((test) => test.held_out).map((test) => test.source_module)
  return [...scored, ...data.not_in_night]
}

/** The selection with one test ticked or unticked. */
export function toggled(selected: ReadonlySet<string>, module: string): Set<string> {
  const next = new Set(selected)
  if (!next.delete(module)) next.add(module)
  return next
}

/** Every test of the night, plus whatever else is already ticked. */
export function withAllScored(
  selected: ReadonlySet<string>,
  data: HeldOutTestsResponse,
): Set<string> {
  return new Set([...selected, ...data.tests.map((test) => test.source_module)])
}

/** Whether the selection differs from what is saved. */
export function isChanged(selected: ReadonlySet<string>, data: HeldOutTestsResponse): boolean {
  const saved = savedSelection(data)
  return saved.length !== selected.size || saved.some((module) => !selected.has(module))
}

/** How many of the night's tests are ticked. */
export function scoredHeldOut(selected: ReadonlySet<string>, data: HeldOutTestsResponse): number {
  return data.tests.filter((test) => selected.has(test.source_module)).length
}

/**
 * A repository path split for display: the file name leads and the folder
 * sits under it, so a long list of tests in one folder scans by name.
 */
export function splitPath(module: string): { folder: string; name: string } {
  const cut = module.lastIndexOf('/') + 1
  return { folder: module.slice(0, cut), name: module.slice(cut) }
}
